/* tslint:disable: max-file-line-count */
import * as React from 'react';

import {
  IGeocodedForSearchResponseSchema,
} from '@cian/api-models/monolith-csharp/api/geo/geocoded-for-search';

import { isEmpty, pipe, isNil } from 'ramda';
import  { getEventValue, clearSafe, debounce } from '@cian/newbuilding-utils';
import * as yamaps from '../../../utils/yamaps';
import { makeSuggestionRequester } from '../../../api/geo';
import { Category } from './List';
import { ClickOutside } from '../../outside';
import { Input } from '../../Input';
import { Icon, TIconType } from '../../Icon';
import { getCachedGeocode } from '../../../api/geo/get-cached-geocode';
import { getGeocodedSearchData } from '../../../api/geo/get-geocoded-search-data';
import { inRangeMemo } from '../../../utils/functional';
import { getSearchAutocomplete } from '../../../api/geo/ab/get-search-autocomplete';
import {
  extractSuggestion,
  IGeoSuggestResponseScheme,
  INewbuildingStructuredSuggestions,
  ISearchAutocompleteUndergroundLine,
} from '../../../containers/Filters/GeoInput/helpers';
import { MAX_ELEMENTS_IN_GEOSUGGEST_GROUP } from '../../../constants/config';

import * as styles from './index.css';
import { createPolygon } from '../../../utils/geo/createPolygon';
import { DEFAULT_INPUT_TIMEOUT } from '../../../constants/timeouts';

export interface IUndergroundDetails {
  lineColor: string;
  undergroundId: number;
}

export interface IGeocodedForSearchUndegroundData {
  undergroundDetails: Array<IUndergroundDetails>;
}

type TBoundedBy = YMaps.ISuggestOptions['boundedBy'];

export interface ISuggestionGroup extends YMaps.ISuggestResult {
  title: string;
  url?: string;
  fullAddress?: string;

  undergroundLines?: Array<IUndergroundDetails>;
  lines?: Array<ISearchAutocompleteUndergroundLine>;
}

export interface ISuggestProps extends React.HTMLProps<HTMLElement> {
  boundedBy?: TBoundedBy;
  subdomain?: string;
  locationId: number;
  ariaLabel?: string;
  onSuggestionSelect(suggestion: TSuggestionSelectedItem): void;
}

export interface ISuggestState {
  loading: boolean;
  addressGroup: Array<ISuggestionGroup>;
  newbuildingGroup: Array<ISuggestionGroup>;
  builderGroup: Array<ISuggestionGroup>;
  undergroundGroup: Array<ISuggestionGroup>;
  districtsGroup: Array<ISuggestionGroup>;
  pure: boolean;
  term: string;
  selectedIndex: number;
}

export interface ISuggestionGeoItem {
  type: 'geo';
  data: IGeocodedForSearchResponseSchema & IGeocodedForSearchUndegroundData;
  dataType: 'geocode';
  polygon?: IPolygonItem;
}

export interface ISuggestionSimpleItem {
  type: string;
  data: ISuggestionGroup & YMaps.ISuggestResult;
  dataType: 'simple';
}

export interface IPolygonItem {
  type: string;
  value: Array<object>;
}

export type TSuggestionSelectedItem = ISuggestionGeoItem | ISuggestionSimpleItem;

export class Suggest extends React.PureComponent<ISuggestProps, ISuggestState> {
  public state: Readonly<ISuggestState> = {
    addressGroup: [],
    newbuildingGroup: [],
    loading: false,
    pure: true,
    selectedIndex: 0,
    term: '',
    builderGroup: [],
    undergroundGroup: [],
    districtsGroup: [],
  };

  public render() {
    const {term, selectedIndex, pure} = this.state;
    const indSelected = this.selections()[selectedIndex];

    return (
      <ClickOutside onOutside={this.toggleSuggestBlur}>
        <div data-mark="SuggestInput" className={styles['suggest_input']}>
          <Input {...this.inputProps()} />
        </div>
        <div data-mark="Suggest" className={styles['suggest_list_handler']}>
          {!pure &&
            <div className={styles['suggest_list']}>
              <div className={styles['suggest_list_input_handler']}>
                <Input {...this.inputProps(true)} />
                <span
                  onClick={() => { this.setTerm(''); this.setPure(true); }}
                  className={styles['suggest_list_close_icon']}
                >
                  <Icon type="close" color="blue" size={16} />
                </span>
              </div>

              {this.allCategories().map(([group, label, icon]) => (
                !isEmpty(group) &&
                  <Category
                    key={`${group}_${label}`}
                    label={label}
                    suggests={group}
                    icon={icon}
                    selected={
                      // если тип первого элемента текущей группы равен типу элемента выбранной группы
                      !isNil(indSelected) && indSelected.type === group[0].type ?
                        indSelected.value :
                        undefined
                    }
                    onHover={this.handleHover}
                    onSelect={this.handleSelect}
                    highlight={term}
                  />
              ))}
            </div>
          }
        </div>
      </ClickOutside>
    );
  }

  private inputProps(autoFocus: boolean = false) {
    const { term, selectedIndex, loading } = this.state;
    const { placeholder, ariaLabel } = this.props;

    return {
      autoFocus,
      onChange: pipe(
        getEventValue, // pick value
        this.prepareValue, // clear and convert layout
        clearSafe(this.debounceMakeSuggestions),
      ),
      onKeyArrowDown: () => this.addSelectedIndex(1),
      onKeyArrowUp: () => this.addSelectedIndex(-1),
      onKeyEnter: () => this.handleSelect(this.selections()[selectedIndex]),
      onKeyEscape: this.toggleSuggestBlur,
      onFocus: this.toggleSuggestFocus,
      onKeyTab: () => this.handleSelect(this.selections()[selectedIndex]),
      placeholder,
      ['aria-label']: ariaLabel || '',
      sync: loading,
      value: term,
    };
  }

  private selections() {
    return this.allCategories().reduce((acc, [group]) => acc.concat(group), [] as ISuggestionGroup[]);
  }

  private allCategories(): Array<[ISuggestionGroup[], string, TIconType]> {
    const {addressGroup, newbuildingGroup, builderGroup, undergroundGroup, districtsGroup} = this.state;

    return [
      [newbuildingGroup, 'Жилые комплексы', 'newbuilding'],
      [undergroundGroup, 'Метро', 'mosmetro'],
      [districtsGroup, 'Районы', 'district'],
      [builderGroup, 'Застройщики', 'builder'],
      [addressGroup, 'Адреса', 'location'],
    ];
  }

  private handleSelect = (suggestion: ISuggestionGroup) => {
    this.setTerm('');
    this.setPure(true);
    if (suggestion.type === 'geo') {
      void this.loadSearchData(suggestion);
    } else if (this.props.onSuggestionSelect) {

      this.props.onSuggestionSelect({
        type: suggestion.type,
        data: suggestion,
        dataType: 'simple',
      });
    }
  }

  private handleHover = (value: ISuggestionGroup) => {
    const ind = this.selections().findIndex((d) => d.value === value.value);
    this.setState({selectedIndex: ind});
  }

  private prepareValue = (value: string): string => {
    this.setTerm(value);

    return value.trim();
  }

  private toggleSuggestBlur = () => {
    this.setPure(true);
  }

  private toggleSuggestFocus = () => {
    this.setPure(false);
  }

  private clearState = () => {
    this.setState({
      addressGroup: [],
      builderGroup: [],
      undergroundGroup: [],
      districtsGroup: [],
      newbuildingGroup: [],
      selectedIndex: 0,
    });
  }

  private makeAddressSuggestions = (value: string) => {
    const {boundedBy} = this.props;
    this.setLoading(true);
    makeSuggestionRequester({boundedBy})(value)
      .then(pipe(
        yamaps.serializeAddress,
        this.setAddressSuggestions,
        () => this.setLoading(false),
      ))
      .catch(() => this.setLoading(false));
  }

  private makeNewbuildingAndBuilderSuggestions = (query: string) => {
    const { locationId } = this.props;

    getSearchAutocomplete({
      query,
      locationId,
      offerType: 'newobject',
      dealType: 'sale',
      regionId: locationId,
    })
      .then((response: IGeoSuggestResponseScheme) => {
        const suggestions: INewbuildingStructuredSuggestions = response.data.suggestions;

        this.setNewbuildingAndBuilderSuggestions(suggestions);
      })
      .catch(() => {});
  }

  private makeSuggestions = (term: string) => {
    this.makeAddressSuggestions(term);
    this.makeNewbuildingAndBuilderSuggestions(term);
  }

  public debounceMakeSuggestions = debounce(DEFAULT_INPUT_TIMEOUT, this.makeSuggestions);

  private setLoading = (loading: boolean) => this.setState({ loading });

  private setTerm = (term: string) => {
    isEmpty(term) ? this.clearState() : this.setPure(false);
    this.setState({term});
  }

  private setNewbuildingAndBuilderSuggestions = (suggestions: INewbuildingStructuredSuggestions) => {
    const newbuildingGroup = extractSuggestion(suggestions, 'newbuildings').slice(0, MAX_ELEMENTS_IN_GEOSUGGEST_GROUP);
    const undergroundGroup = extractSuggestion(suggestions, 'undergrounds').slice(0, MAX_ELEMENTS_IN_GEOSUGGEST_GROUP);
    const districtsGroup = extractSuggestion(suggestions, 'districts').slice(0, MAX_ELEMENTS_IN_GEOSUGGEST_GROUP);
    const builderGroup = extractSuggestion(suggestions, 'builders').slice(0, MAX_ELEMENTS_IN_GEOSUGGEST_GROUP);

    this.setState({ newbuildingGroup, builderGroup, undergroundGroup, districtsGroup });
  }

  private setAddressSuggestions = (addressGroup: ISuggestionGroup[]) => {
    this.setState({
      addressGroup: addressGroup.slice(0, MAX_ELEMENTS_IN_GEOSUGGEST_GROUP),
    });
  }

  private addSelectedIndex = (i: number) => {
    const { selectedIndex = 0 } = this.state;
    const range = inRangeMemo(0, this.selections().length - 1);
    this.setState({selectedIndex: range(selectedIndex + i)});
  }

  private setPure = (pure: boolean) => this.setState({ pure });

  private loadSearchData = async (item: ISuggestionGroup) => {
    try {
      const {subdomain} = this.props;
      this.setLoading(true);

      const getCachedGeocodeResponse = await getCachedGeocode({request: item.displayName}, subdomain);
      const firstOccurence = !isEmpty(getCachedGeocodeResponse.items) && getCachedGeocodeResponse.items[0];

      if (!firstOccurence) {
        return;
      }

      const {text, kind, coordinates, boundedBy} = firstOccurence;
      const polygon = createPolygon( boundedBy );

      const getGeocodedSearchDataResponse = await getGeocodedSearchData({
        address: text,
        kind,
        lat: coordinates[1],
        lng: coordinates[0],
      }, subdomain);

      this.setLoading(false);
      if (getGeocodedSearchDataResponse.text && this.props.onSuggestionSelect) {

        const result: TSuggestionSelectedItem = {
          type: 'geo',
          data: (getGeocodedSearchDataResponse as IGeocodedForSearchResponseSchema & IGeocodedForSearchUndegroundData),
          dataType: 'geocode',
          polygon: {
            type: 'terms',
            value: [...polygon],
          },
        };

        this.props.onSuggestionSelect(result);
      }
    } catch (e) {
      this.setLoading(false);
    }
  }
}
